import type { LatLng, LatLngBounds } from 'leaflet';
import { debounce, pipe } from 'overmind';

import type { Context } from '@/overmind';
import Extent from '@arcgis/core/geometry/Extent';
import Point from '@arcgis/core/geometry/Point';
import Multipoint from '@arcgis/core/geometry/Multipoint';
import Polyline from '@arcgis/core/geometry/Polyline';
import Polygon from '@arcgis/core/geometry/Polygon';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import MapView from '@arcgis/core/views/MapView';
import type { PlainGeometryJSON } from '.';
import { IHighlightFeature, IStopHighlightingFeature } from '.';
import { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';
import { geojsonToArcGIS } from '@terraformer/arcgis';
import Graphic from '@arcgis/core/Graphic';
import { mapGeojsonToArcGisGeometry } from '@/overmind/actions';
import { highlightElement } from '@/features/build-calculation-context/components/Home/components/ArcGisMapContainer/Map';

type MapCenterPayload = {
  zoom: number;
  center: LatLng;
  bounds: LatLngBounds
};

export const setMapCenter = pipe(
  ({ state }: Context, { zoom, center, bounds }: MapCenterPayload) => {
    if (state.map.zoom !== zoom || state.map.center !== center) {
      state.map.zoom = zoom;
      state.map.center = center;
      state.map.bounds = bounds;
    }
  }
);

export const resetMapCenter = pipe(({ state }: Context) => {
  state.map.zoom = undefined;
  state.map.center = undefined;
});

export const backgroundMapReady = (context: Context) => {
  context.state.map.backgroundMapReady = 'READY';
};

export const backgroundMapNotReady = (context: Context) => {
  context.state.map.backgroundMapReady = 'NOT_READY';
};

const reconstructGeometry = (json: PlainGeometryJSON): __esri.Extent | __esri.Point | __esri.Polygon | __esri.Multipoint | __esri.Polyline => {
  switch (json.type) {
    case 'point': return Point.fromJSON(json);
    case 'multipoint': return Multipoint.fromJSON(json);
    case 'polyline': return Polyline.fromJSON(json);
    case 'polygon': return Polygon.fromJSON(json);
    default: return Extent.fromJSON(json);
  }
};

export const withExtent = pipe(
  debounce(50),
  async (context: Context, payload: { goTo: (extent: __esri.Extent | __esri.Point | __esri.Polygon | __esri.Multipoint | __esri.Polyline) => Promise<void>}) => {
    if(!context.state.map.initialExtent) return;
    const geometry = reconstructGeometry(context.state.map.initialExtent);
    context.state.map.initialExtent = undefined;
    await payload.goTo(geometry);
  }
);

class ArcGisView implements IStopHighlightingFeature, IHighlightFeature {
  view: () => MapView;
  constructor(view: __esri.MapView) {
    this.view = () => view;
  }
  highlight(features: FeatureWithIDs<unknown>): void {
    try {
      const geometry = mapGeojsonToArcGisGeometry(features)
      if(!geometry) return;
      const g = geojsonToArcGIS(features) as Graphic;
      g.geometry.spatialReference = new SpatialReference({ wkid: 25832 });
      const graphic = new Graphic({
        geometry,
        attributes: g.attributes
      });
      if(!graphic) return;
      this.waitForAllLayers(() => {
        highlightElement([graphic], this.view());
      }) 

      this.view().when(() => {
        highlightElement([graphic], this.view());
      })

    } catch (error) {
      console.error(error);
    }
  }
  stopHighlighting() {
    const graphics = this.view().graphics;
    console.log({ graphics: graphics.toArray() });
    graphics.removeAll();

    return this
  }
  private waitForAllLayers(callback: () => void) {
    this.view().on("layerview-create", callback);
  }
}

export const onViewInitialized = (context: Context, payload: { view: MapView }) => {
  context.state.map.view = new ArcGisView(payload.view);
  if(context.state.selectedFeature.current) {
    context.state.map.view.highlight(context.state.selectedFeature.current);
  }
}

export const highlight = (context: Context, feature: FeatureWithIDs<unknown>) => {
  if(context.state.map.view) {
    context.state.map.view.highlight(feature);
  }
}

export const stopHighlighting = (context: Context) => {
  if(context.state.map.view) {
    context.state.map.view.stopHighlighting();
  }
}
